import {map} from 'lodash';
import cx from 'classnames';
import {Group} from '@visx/group';
import {observer} from 'mobx-react';
import {FC, useMemo, MouseEvent, ReactNode} from 'react';

import {CELL_LABEL_PADDING, CellDataType} from './Honeycomb';
import './Hexagon.less';

type HexagonProps = {
  itemId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  cellOffset: number;
  rowId: number;
  cellId: number;
  faded: boolean;
  labelClassNames: string;
  label: ReactNode | string;
  item: object;
  color: string;
  onMouseOver: (e: MouseEvent, cellData: CellDataType) => void;
  onMouseOut: (e: MouseEvent) => void;
  onClick: (e: MouseEvent, cellData: CellDataType) => void;
};

const Hexagon: FC<HexagonProps> = ({item, itemId, x, y, width, height, faded, color, cellOffset,
  label, labelClassNames, rowId, cellId, onMouseOver, onMouseOut, onClick}) => {
  const {polygonPoints, cellData} = useHexagon({width, height, itemId, rowId, cellId, item, faded, label, color});

  return (
    <Group
      className='hexagon'
      transform={`translate(${x}, ${y})`}
    >
      <polygon
        className={cx(`cell-color-${color}`, {faded})}
        style={{strokeWidth: 2 * cellOffset}}
        points={polygonPoints}
        onMouseOver={(e) => onMouseOver(e, cellData)}
        onMouseOut={onMouseOut}
        onClick={(e) => onClick(e, cellData)}
      />
      <foreignObject
        className='label-container'
        width={width}
        height={height}
      >
        <div
          style={{width: width - 2 * CELL_LABEL_PADDING}}
          className={cx(labelClassNames)}
        >
          {label}
        </div>
      </foreignObject>
    </Group>
  );
};

export default observer(Hexagon);

const useHexagon = ({width, height, itemId, rowId, cellId, item, faded, label, color}) => {
  const radius = height / 2;

  const polygonPoints = useMemo(() => {
    return map(new Array(6).fill(null), (_, i) => {
      const angleInDegrees = 60 * i - 30;
      const angleInRadians = (Math.PI / 180) * angleInDegrees;
      return `${width / 2 + radius * Math.cos(angleInRadians)},${height / 2 + radius * Math.sin(angleInRadians)}`;
    }).join(' ');
  }, [width, radius, height]);

  const cellData: CellDataType = useMemo(
    () => ({id: itemId, rowId, cellId, data: item, faded, label, color}),
    [itemId, rowId, cellId, item, faded, label, color]
  );

  return {polygonPoints, cellData};
};
